
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, RefreshCcw, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import PromptGrid from "@/components/prompts/PromptGrid";
import LoadingState from "@/components/explore/LoadingState";
import EmptyState from "@/components/explore/EmptyState";
import type { Prompt } from "@/services/promptService";

interface PopularPromptsProps {
  prompts: Prompt[];
  isLoading: boolean;
  error?: Error | null;
  refetch?: () => void;
}

const PopularPrompts: React.FC<PopularPromptsProps> = ({ 
  prompts, 
  isLoading, 
  error,
  refetch 
}) => {
  console.log("Popular prompts rendering with:", { 
    promptsLength: prompts?.length || 0, 
    isLoading, 
    hasError: !!error 
  });
  
  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-6xl">
        <div className="flex justify-between items-center mb-12">
          <div>
            <h2 className="text-3xl font-bold">Popular Prompts</h2>
            <p className="mt-3 text-muted-foreground">
              Top-rated prompts sorted by usage, reviews, and engagement
            </p>
          </div>
          <div className="flex gap-2">
            {refetch && (
              <Button onClick={refetch} variant="outline" size="icon" className="h-9 w-9" title="Refresh prompts">
                <RefreshCcw className="h-4 w-4" />
              </Button>
            )}
            <Button asChild variant="outline">
              <Link to="/explore" className="flex items-center gap-2">
                View All
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
        
        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <div className="text-center py-12 border border-dashed border-muted-foreground/20 rounded-lg">
            <p className="text-muted-foreground mb-4">Error loading prompts</p>
            {refetch && (
              <Button onClick={refetch} variant="outline" size="sm">
                Try Again
              </Button>
            )}
          </div>
        ) : prompts && prompts.length > 0 ? (
          <PromptGrid prompts={prompts} />
        ) : (
          <div className="text-center py-12 border border-dashed border-muted-foreground/20 rounded-lg">
            <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-muted-foreground mb-4">No prompts available at the moment</p>
            {refetch && (
              <Button onClick={refetch} variant="outline" size="sm">
                Refresh Prompts
              </Button>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default PopularPrompts;
